import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import Shell from "@/components/Shell";
import StatusBadge from "@/components/StatusBadge";
import { Plus, ArrowUpRight, FileText, MessageCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/lib/brand";

export default function ClientDashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cases");
      setCases(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-10">
        {/* Greeting card */}
        <div className="rounded-[28px] bg-gradient-to-br from-[#0A2540] via-[#0D2A4D] to-[#11365E] text-white p-8 md:p-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-dot-grid opacity-10" />
          <div className="grid md:grid-cols-12 gap-8 items-center relative">
            <div className="md:col-span-8">
              <div className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Welcome back</div>
              <h1 data-testid="client-greeting" className="font-heading font-bold text-4xl md:text-5xl mt-2 leading-tight">
                Hello, {user?.name?.split(" ")[0] || "Client"}.
              </h1>
              <p className="mt-3 text-slate-300 max-w-xl">
                Track your case, upload documents and read your expert opinion — all in one place.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button data-testid="client-new-case-btn" onClick={() => nav("/dashboard/new")} className="btn-gold text-sm">
                  <Plus className="w-4 h-4" /> New case
                </button>
                <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" className="btn-emerald text-sm" data-testid="client-whatsapp-btn">
                  <MessageCircle className="w-4 h-4" /> Talk to your case manager
                </a>
              </div>
            </div>
            <div className="md:col-span-4 grid grid-cols-2 gap-3">
              <KPI label="Total" value={cases.length} />
              <KPI label="In progress" value={cases.filter((c) => c.status !== "PROCESSED").length} />
              <KPI label="Processed" value={cases.filter((c) => c.status === "PROCESSED").length} />
              <KPI label="Awaiting docs" value={cases.filter((c) => c.status === "REGISTERED").length} />
            </div>
          </div>
        </div>

        {/* Cases */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-2xl">Your cases</h2>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              {cases.length} total
            </span>
          </div>

          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : cases.length === 0 ? (
            <div className="brand-card p-12 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-brand/10 text-emerald-brand flex items-center justify-center">
                <FileText className="w-7 h-7" />
              </div>
              <div className="font-heading font-bold text-xl mt-5">No cases yet</div>
              <p className="text-sm text-slate-600 mt-2">Register your first case and our team will get to work.</p>
              <button data-testid="client-empty-new-case-btn" onClick={() => nav("/dashboard/new")} className="btn-emerald text-sm mt-5 inline-flex">
                <Plus className="w-4 h-4" /> Start a case
              </button>
            </div>
          ) : (
            <div className="brand-card overflow-hidden p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-100">
                    <th className="py-3 px-4">Case №</th>
                    <th className="py-3 px-4">Title</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Updated</th>
                    <th className="py-3 px-4 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((c) => (
                    <tr
                      key={c.id}
                      data-testid={`case-row-${c.id}`}
                      className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => nav(`/dashboard/cases/${c.id}`)}
                    >
                      <td className="py-4 px-4 text-xs font-mono text-emerald-brand font-semibold">{c.case_no}</td>
                      <td className="py-4 px-4 font-medium">{c.title}</td>
                      <td className="py-4 px-4"><StatusBadge status={c.status} /></td>
                      <td className="py-4 px-4 text-xs text-slate-500">{formatDate(c.updated_at)}</td>
                      <td className="py-4 px-4 text-right">
                        <ArrowUpRight className="w-4 h-4 inline text-slate-400" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function KPI({ label, value }) {
  return (
    <div className="glass-dark rounded-2xl p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold">{label}</div>
      <div className="font-heading font-bold text-3xl mt-1 text-white">{value}</div>
    </div>
  );
}
